<template>
	<div class="merchant-list-section">
		<div class="container">
			<div style="margin-top: 20px;" v-if="merchantLoading">
				<el-skeleton :loading="merchantLoading" animated :rows="10"></el-skeleton>
			</div>
			<div class="merchant-card" v-for="(merchant, index) in merchants" :key="index">
				<div class="merchant-content">
					<!-- 左边区域显示商家信息 -->
					<div class="merchant-info">
						<div class="store-header">
							<img :src="merchant.storeLogo" :alt="merchant.storeName" class="store-avatar">
							<div class="store-name">
								<div style="display: flex; align-items: center;justify-content: flex-start;">
									<div class="vip-tag-left">
										<img :src="merchant.memberConfigIcon" style="width: 15px;">
										{{merchant.memberConfigName}}
									</div>
									<div class="store-name-text">{{ merchant.storeName }}</div>
								</div>
								<div class="store-stats">
									<span>{{$t('fans')}}: {{ merchant.collectionNum || 0 }}</span>
									<span>{{$t('sold')}}: {{ merchant.storeSales || 0 }}</span>
								</div>
								<div class="store-actions">
									<el-button type="warning" size="mini" plain
										@click="delStoreCollection(merchant.id,index)">{{$t('unfollow')}}</el-button>
									<el-button type="primary" size="mini" plain
										@click="handleDetail(merchant)">{{$t('enter')}}</el-button>
								</div>
							</div>
						</div>
					</div>

					<!-- 右边显示商品信息 -->
					<div class="product-carousel-container">
						<!-- 当商品为空时显示提示 -->
						<div v-if="!merchant.products || merchant.products.length === 0" class="no-products">
							<img src="/icon/common/nodata.png">
							{{$t('noProduct')}}
						</div>
						<!-- 当商品不为空时显示轮播 -->
						<template v-else>
							<button class="thumbnail-btn left-btn" @click="scrollProducts(index, 'left')">
								<i class="el-icon-arrow-left"></i>
							</button>
							<div class="product-carousel">
								<div class="thumbnail-scroll-wrapper">
									<div class="thumbnail-list product-list"
										@touchstart="handleTouchStart($event, index)"
										@touchmove="handleTouchMove($event, index)"
										@touchend="handleTouchEnd($event, index)"
										:style="{ transform: `translateX(${scrollOffsets[index] || 0}px)` }">
										<div class="thumbnail-item product-item" @click="handleProductDetail(product)"
											v-for="(product, productIndex) in merchant.products" :key="productIndex">
											<img :src="product.small" :alt="product.goodsName" class="product-image">
											<div class="product-name">{{ product.goodsName }}</div>
											<p class="product-price">
												<span>{{currencySymbol}}</span>
												<span style="margin-left: 3px;">{{ product.price.toFixed(2) }}</span>
											</p>
										</div>
									</div>
								</div>
							</div>
							<button class="thumbnail-btn right-btn" @click="scrollProducts(index, 'right')">
								<i class="el-icon-arrow-right"></i>
							</button>
						</template>
					</div>
				</div>
			</div>
			<div class="nodata" v-if="merchants.length==0&&!merchantLoading" style="margin-top: 200px;">
				<img src="/icon/common/nodata.png">
				<span>{{$t('noData')}}</span>
			</div>
			<div class="loading">
				<!-- 加载更多状态 -->
				<div v-if="loadingMore" class="loading-more" style="margin: 30px 0; text-align: center;">
					<i class="el-icon-loading"></i>
					<span>{{$t('dataLoading')}}</span>
				</div>

				<!-- 已加载全部数据 -->
				<div v-if="!hasMore&&merchants.length>0" class="load-all"
					style="margin: 30px 0; text-align: center; color: #999;">
					{{$t('loadingFinish')}}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import merchantApi from '@/api/merchant';
	import goodsApi from '@/api/product';
	import collectionApi from '@/api/collection';
	import storage from "@/utils/storage.js";
	export default {
		name: 'MerchantList',
		props: {
			searchKeyword: {
				type: String,
				default: ''
			},
			//距离顶部像素
			scrollTop: {
				type: Number,
				default: 0
			},
			//组件来源
			source: {
				type: String,
				default: 'merchant'
			}
		},
		data() {
			return {
				accessToken: storage.getToken(),
				merchantLoading: true,
				currencySymbol: '',
				merchants: [], //商家信息
				touchStartX: {},
				touchEndX: {},
				scrollOffsets: {},
				params: {
					pageNumber: 1,
					pageSize: 10,
					sort: "createTime",
					order: "desc",
					storeName: ''
				},
				loadingMore: false, // 加载更多的状态
				hasMore: true, // 是否还有更多数据
				isMounted: false // 组件是否已挂载
			}
		},
		mounted() {
			// 组件挂载后添加滚动事件监听器
			this.isMounted = true;
			window.addEventListener('scroll', this.handleScroll);
			// 组件销毁时移除滚动事件监听器
			this.$once('hook:destroyed', () => {
				window.removeEventListener('scroll', this.handleScroll);
			});
		},
		methods: {
			init() {
				this.merchantLoading = true;
				this.merchants = [];
				this.params.pageNumber = 1;
				this.hasMore = true;
				this.currencySymbol = storage.getCurrencySymbol();
				window.scrollTo({
					top: this.scrollTop,
					behavior: 'smooth'
				})
				this.getStoreList();
			},
			async getStoreList() {
				try {
					this.params.storeName = this.searchKeyword || '';
					var merchants = null;
					var total = 0;
					if (this.source == 'merchant') {
						const res = await merchantApi.getStoreList(this.params);
						merchants = res.result.records;
						total = res.result.total;
					} else {
						const res = await collectionApi.getStoreCollectionList(this.params);
						merchants = res.result.records;
						total = res.result.total;
					}

					// 为每个商家获取商品列表
					const merchantsWithProducts = await Promise.all(
						merchants.map(async (merchant) => {
							try {
								const goodsRes = await goodsApi.getStoreGoodsList({
									pageNumber: 1,
									pageSize: 20,
									storeId: merchant.id,
									marketEnable: "UPPER",
									authFlag: "PASS",
									sort: "releaseTime",
									order: "desc",
									rateCode: storage.getCurrency()
								});
								return {
									...merchant,
									products: goodsRes.result.records
								};
							} catch (error) {
								console.error(`获取商家 ${merchant.storeName} 商品失败:`, error);
								return {
									...merchant,
									products: []
								};
							}
						})
					);

					// 检查是否是第一页加载
					if (this.params.pageNumber === 1) {
						// 第一页直接替换数据
						this.merchants = merchantsWithProducts;
					} else {
						// 后续页追加数据
						this.merchants = [...this.merchants, ...merchantsWithProducts];
					}

					if (this.merchants.length >= total) {
						this.hasMore = false;
					}

					// 重置加载状态
					this.merchantLoading = false;
					this.loadingMore = false;
				} catch (error) {
					console.error('获取商家列表失败:', error);
					if (this.params.pageNumber === 1) {
						this.merchants = [];
					}
					// 重置加载状态
					this.merchantLoading = false;
					this.loadingMore = false;
				}
			},
			// 加载更多数据
			loadMore() {
				if (this.loadingMore || !this.hasMore || this.merchantLoading) {
					return;
				}

				this.loadingMore = true;
				this.params.pageNumber++;
				this.getStoreList();
			},

			// 处理滚动事件
			handleScroll() {
				if (!this.isMounted || this.loadingMore || !this.hasMore) {
					return;
				}

				const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
				const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
				const clientHeight = document.documentElement.clientHeight || window.innerHeight;

				// 当距离底部小于230px时，加载更多数据
				if (scrollHeight - scrollTop - clientHeight < 230) {
					this.loadMore();
				}
			},
			// 跳转到商家内页
			handleDetail(row) {
				window.open(`/merchant-detail?id=${row.id}&keyword=${this.searchKeyword.trim()}`)
			},
			handleProductDetail(row) {
				window.open(`/product-detail?goodsId=${row.id}`);
			},
			// 触摸事件处理
			handleTouchStart(event, index) {
				this.touchStartX[index] = event.changedTouches[0].screenX
			},
			handleTouchMove(event, index) {
				this.touchEndX[index] = event.changedTouches[0].screenX
			},
			handleTouchEnd(event, index) {
				// 计算滑动距离
				const threshold = 50 // 滑动阈值
				const touchStartX = this.touchStartX[index] || 0
				const touchEndX = this.touchEndX[index] || 0
				const diff = touchEndX - touchStartX

				// 根据滑动方向执行滚动
				if (diff > threshold) {
					// 向右滑动，滚动到左边
					this.scrollProducts(index, 'left')
				} else if (diff < -threshold) {
					// 向左滑动，滚动到右边
					this.scrollProducts(index, 'right')
				}
			},
			scrollProducts(index, direction) {
				// 使用CSS transform实现产品列表的左右滚动效果
				// 初始化滚动偏移量
				if (!this.scrollOffsets[index]) {
					this.$set(this.scrollOffsets, index, 0)
				}

				// 获取产品项的宽度（包括margin）
				const productItemWidth = 540 + 20 // 产品宽度 + margin-right
				const scrollAmount = productItemWidth

				// 计算新的滚动偏移量
				let newOffset = this.scrollOffsets[index]
				if (direction === 'left') {
					// 向右滚动（偏移量增加）
					newOffset += scrollAmount
				} else {
					// 向左滚动（偏移量减少）
					newOffset -= scrollAmount
				}

				// 限制滚动范围在合理区间
				const maxOffset = 0 // 最大偏移量（不超出左侧）
				const minOffset = -((140 + 20) * this.merchants[index].products.length - 700) // 最小偏移量（不超出右侧，700是容器宽度）
				newOffset = Math.max(minOffset, Math.min(maxOffset, newOffset))

				// 更新滚动偏移量
				this.$set(this.scrollOffsets, index, newOffset)
			},
			//取消关注
			async delStoreCollection(storeId, index) {
				if (!this.accessToken) {
					this.handleLogin();
				} else {
					const res = await collectionApi.delStoreCollection(storeId);
					if (res.code == 200) {
						this.$message.success(this.$t('followCancel'))
						this.merchants.splice(index, 1)
					} else {
						this.$message.warning(this.$t('followFail'))
					}
				}
			},
			handleLogin() {
				this.$confirm(this.$t('loginMessage'), this.$t('tip'), {
					confirmButtonText: this.$t('gotoLogin'),
					cancelButtonText: this.$t('closeLogin'),
					type: 'warning'
				}).then(() => {
					this.$router.push('/login')
				}).catch(() => {
					// 处理取消事件，避免报错
				})
			}
		}
	}
</script>

<style scoped>
	.merchant-list-section {
		background-color: #fff;
	}

	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.merchant-card {
		padding: 20px;
		border-bottom: 1px solid #E6F1FE;
		overflow: hidden;
		height: 255px;
	}

	.merchant-content {
		display: flex;
		justify-content: space-between;
	}

	/* Merchant Info Styles (matching Detail.vue store-info) */
	.merchant-info {
		width: 400px;
		position: relative;
		flex-shrink: 0;
		display: flex;
		align-items: center;
	}

	.store-header {
		display: flex;
		align-items: flex-start;
	}

	.store-avatar {
		width: 100px;
		height: 100px;
		border-radius: 10px;
		object-fit: cover;
		margin-right: 15px;
	}

	.store-name {
		flex: 1;
		min-width: 0;
	}

	.store-name>div:first-child {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;
	}

	.store-name-text {
		font-size: 16px;
		font-weight: bold;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		width: 180px;
	}

	.store-stats {
		font-size: 14px;
		color: #666;
		margin-bottom: 10px;
	}

	.store-stats span {
		margin-right: 15px;
	}

	.store-actions {
		margin-top: 10px;
	}

	/* Product Carousel Styles */
	.product-carousel-container {
		position: relative;
		width: 700px;
	}

	.product-carousel {
		width: 100%;
		overflow: hidden;
	}

	.thumbnail-scroll-wrapper {
		overflow: hidden;
	}

	.thumbnail-list {
		display: flex;
		transition: transform 0.3s ease;
	}

	.thumbnail-item {
		flex-shrink: 0;
		width: 140px;
		margin-right: 20px;
		text-align: center;
		cursor: pointer;
	}

	.product-image {
		width: 140px;
		height: 140px;
		border: 1px solid #f2f2f2;
		object-fit: contain;
		border-radius: 8px;
		margin-bottom: 8px;
	}

	.product-name {
		font-size: 14px;
		color: #333;
		margin-bottom: 10px;
		height: 38px;
		line-height: 20px;
		overflow: hidden;
		text-overflow: ellipsis;
		text-align: left;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.product-price {
		font-size: 18px;
		color: #F59A23;
		margin: 0;
		font-weight: bold;
		text-align: left
	}

	/* 暂无商品提示样式 */
	.no-products {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		height: 255px;
		/* 与商品列表高度一致 */
		width: 100%;
		font-size: 16px;
		color: #aaa;
	}

	.no-products img {
		width: 100px;
		height: 100px;
		margin-bottom: 15px;
	}

	.thumbnail-btn {
		position: absolute;
		top: 31%;
		transform: translateY(-50%);
		width: 30px;
		height: 30px;
		border-radius: 50%;
		background-color: rgba(255, 255, 255, 0.8);
		border: 1px solid #ddd;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		z-index: 999;
		transition: all 0.3s ease;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
	}

	.thumbnail-btn:hover {
		background-color: #fff;
		border-color: #999;
	}

	.left-btn {
		left: -15px;
		z-index: 99;
	}

	.right-btn {
		right: -15px;
		z-index: 99;
	}

	@keyframes rotating {
		from {
			transform: rotate(0deg);
		}

		to {
			transform: rotate(360deg);
		}
	}
</style>